import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { FaX } from 'react-icons/fa6';
import { useQuery } from 'react-query';
import { toast, ToastContainer } from 'react-toastify';
import z from 'zod';
import { getReturnMailReason } from '../api/Network';
import MultiSelectDropdown from '../components/MultiSelectDropdown';
import { useToastStyle } from '../hooks/useToastStyle';

export const FilterSchema = z.object({
  reasons: z.string().optional(),
});
export type FilterFormData = z.infer<typeof FilterSchema>;
export type Reason = {
  id: number;
  description: string;
  name: string;
};
type Props = {
  setReasonFilter: React.Dispatch<React.SetStateAction<string[]>>;
  setCompanyNameFilter?: React.Dispatch<React.SetStateAction<string[]>>;
  setStateFilter?: React.Dispatch<React.SetStateAction<string[]>>;
  reasonFilter?: string[];
  companyName?: Record<string, number>;
  states?: Record<string, number>;
};
export const FilterModal = (props: Props) => {
  const {
    setReasonFilter,
    setCompanyNameFilter,
    setStateFilter,
    reasonFilter = [],
    companyName = {},
    states = {},
  } = props;
  const [localReasonFilter, setLocalReasonFilter] = useState<string[]>(reasonFilter);
  const [localCompanyNameFilter, setLocalCompanyNameFilter] = useState<string[]>([]);
  const [localStateFilter, setLocalStateFilter] = useState<string[]>([]);
  const toastStyle = useToastStyle();

  const { handleSubmit, reset } = useForm<FilterFormData>({
    resolver: zodResolver(FilterSchema),
    mode: 'onTouched',
  });
  const { data: reasons } = useQuery(['reasons'], () => getReturnMailReason(), {
    onError: (error: Error) => toast.error(error.message),
    refetchOnWindowFocus: false,
    refetchInterval: 3600000,
  });

  const closeModal = (e?: React.MouseEvent<HTMLButtonElement | HTMLFormElement>) => {
    e?.preventDefault();
    (document.getElementById('filter_modal') as HTMLFormElement)?.close();
  };

  const onSubmit = async (data: FilterFormData) => {
    setReasonFilter(localReasonFilter);
    setCompanyNameFilter?.(localCompanyNameFilter);
    setStateFilter?.(localStateFilter);
    toast.success('Filter applied');
    reset();
    closeModal();
  };

  const handleReasonChange = (selected: string[]) => {
    setLocalReasonFilter(selected);
  };

  const handleCompanyNameChange = (selected: string[]) => {
    setLocalCompanyNameFilter(selected);
  };

  const handleStateChange = (selected: string[]) => {
    setLocalStateFilter(selected);
  };

  const handleClear = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setLocalReasonFilter([]);
    setLocalCompanyNameFilter([]);
    setLocalStateFilter([]);
    toast.success('Filter cleared', { containerId: 'modal-filter' });
  };
  return (
    <dialog className="modal" id="filter_modal">
      <div className="modal-box w-full max-w-2xl bg-base-100 flex flex-col max-h-[90vh] overflow-visible">
        {/* Header - Fixed */}
        <div className="pb-4 flex-shrink-0">
          <div className="flex flex-row justify-between">
            <div className="text-3xl font-bold text-primary">Filter Records</div>
            <button type="button" className="text-error" onClick={(e) => closeModal(e)}>
              <FaX size={20} />
            </button>
          </div>
          <div className="divider divider-primary my-2"></div>
        </div>

        {/* Form - Not scrollable, dropdown handles its own scrolling */}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 flex-shrink-0 overflow-x-visible">
          {/* Reasons Filter Section */}
          <div className="form-control w-full">
            <label className="label pb-2">
              <span className="label-text text-lg font-semibold text-primary">Return Reasons</span>
            </label>
            {reasons && (
              <div className="bg-base-200 p-4 rounded-lg flex flex-row gap-4">
                <MultiSelectDropdown
                  hintText="Select reasons"
                  options={reasons.data.map((d) => ({
                    value: d.name,
                    label: d.name,
                  }))}
                  selected={localReasonFilter}
                  onChange={handleReasonChange}
                />
                <MultiSelectDropdown
                  hintText="Company Name"
                  options={Object.keys(companyName).map((key) => ({
                    value: key,
                    label: key,
                  }))}
                  selected={localCompanyNameFilter}
                  onChange={handleCompanyNameChange}
                />
                <MultiSelectDropdown
                  hintText="State"
                  options={Object.keys(states).map((key) => ({
                    value: key,
                    label: key,
                  }))}
                  selected={localStateFilter}
                  onChange={handleStateChange}
                />
              </div>
            )}
          </div>
        </form>

        {/* Action Buttons - Fixed at Bottom */}
        <div className="modal-action gap-3 pt-6 border-t border-base-300 flex-shrink-0">
          <button type="submit" className="btn btn-success btn-sm " onClick={handleSubmit(onSubmit)}>
            Apply Filter
          </button>
          <button
            className="btn btn-outline btn-info btn-sm "
            disabled={
              localReasonFilter.length === 0 && localCompanyNameFilter.length === 0 && localStateFilter.length === 0
            }
            onClick={handleClear}>
            Clear Filter
          </button>
        </div>
        <div className="fixed inset-0 pointer-events-none">
          <div className="pointer-events-auto">
            <ToastContainer containerId={'modal-filter'} position="top-center" toastStyle={toastStyle} />
          </div>
        </div>
      </div>

      <form method="dialog" className="modal-backdrop" onClick={(e) => closeModal(e)}>
        <button>close</button>
      </form>
    </dialog>
  );
};
